import { CHARGEBEE_SITE } from '@/lib/env'
import type { ChargebeeInstance } from '@/types/chargebee'

const SCRIPT_SRC = 'https://js.chargebee.com/v2/chargebee.js'

let scriptPromise: Promise<void> | null = null
let instance: ChargebeeInstance | null = null

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`)
    if (existing) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Chargebee.js'))
    document.head.appendChild(script)
  })
  return scriptPromise
}

// Lazily loads Chargebee.js and returns a singleton, initialized instance — callers don't need
// to worry about the script tag or re-initializing on every "Manage Subscription" click.
export async function getChargebeeInstance(): Promise<ChargebeeInstance> {
  if (instance) return instance
  if (!CHARGEBEE_SITE) throw new Error('VITE_CHARGEBEE_SITE is not configured')

  await loadScript()
  if (!window.Chargebee) throw new Error('Chargebee.js loaded but window.Chargebee is unavailable')

  instance = window.Chargebee.init({ site: CHARGEBEE_SITE })
  return instance
}

const TEAM_SWITCH_KEY = 'rolla_chargebee_team_switched'

// Chargebee.js's own portal instance (returned by createChargebeePortal()) carries forward
// internal session/customer state across opens — confirmed in practice: switching teams via
// BrandSwitcher and then opening Manage Subscription showed the previous team's billing portal,
// even though our own fetchPortalSession call is fully correct (fresh selectedTeamId every
// time). Chargebee's own engineering team has confirmed re-initializing an active instance isn't
// supported by the SDK (github.com/chargebee/chargebee-js-wrappers/issues/65) — a fresh
// Chargebee.init() call doesn't clear it, only a real page reload does. So BrandSwitcher calls
// this on every team switch, and SettingsPage (the only place Manage Subscription lives) checks
// it on mount via reloadIfTeamSwitched and force-reloads once if set, before the user could ever
// click through to a stale portal. sessionStorage (not a JS variable) specifically because it
// needs to survive the reload itself.
export function markTeamSwitchedForChargebee(): void {
  sessionStorage.setItem(TEAM_SWITCH_KEY, '1')
}

// Returns true and triggers a reload if a team switch happened since the last time this ran —
// call once on mount of any page that can open the Chargebee portal. Clears the flag first so
// the reload's own remount doesn't loop.
export function reloadIfTeamSwitched(): boolean {
  if (!sessionStorage.getItem(TEAM_SWITCH_KEY)) return false
  sessionStorage.removeItem(TEAM_SWITCH_KEY)
  window.location.reload()
  return true
}
